import {
  CustomerStatisticsByFilterItemsFragment,
  ExpectedStatGroupVolumeItemsFragment,
  FetchStatBoxesQuery,
  FetchStatBoxesQueryVariables,
} from "@/gql/graphql";
import { gql } from "@apollo/client";
import { apolloClient } from "../../../apollo-client/ApolloClient";
import { AppThunk } from "@/redux/RootReducer";
import { statBoxesRedux } from "../redux";
import { calculateCustomerRouteStatisticsTable } from "../transformers/StatBoxDataTransformer";
import { mergeStatBoxQueryData } from "../transformers/StatBoxQueryDataMerger";

const query = gql`
  fragment CustomerStatisticsByFilterItems on CustomerStatisticsEntry {
    id
    expectedVolume
    statVolume12Months
    unitCategoryCode
    expectedStatVolume
    sailingCategoryCode
    volume12Months
    actualOutcomeInPercent
    targetInPercent
  }
  fragment expectedStatGroupVolumeItems on ExpectedStatGroupVolume {
    sailingCategoryCode
    unitCategoryCode
    expectedStatGroupVolume
  }
  query FetchStatBoxes($customerId: ID!, $routePairId: ID!, $year: Int!) {
    productPrice {
      customers {
        byId(id: $customerId) {
          id
          expectedStatGroupVolume(
            negotiationYear: $year
            routePairCodes: [$routePairId]
          ) {
            volumes {
              ...expectedStatGroupVolumeItems
            }
          }
          statisticsByFilter(routePairId: $routePairId, year: $year) {
            items {
              ...CustomerStatisticsByFilterItems
            }
          }
        }
      }
    }
  }
`;
export const clearAndFetchStatBoxes =
  (params: FetchStatBoxesQueryVariables): AppThunk =>
  async (dispatch) => {
    dispatch(statBoxesRedux.actions.clearAll());
    dispatch(statBoxesRedux.actions.setQueryParams(params));
    await dispatch(refetchStatBoxes());
  };

export const refetchStatBoxes = (): AppThunk => async (dispatch, getState) => {
  dispatch(statBoxesRedux.actions.setLoading());

  const queryParams = getState().statBoxes.queryParams;

  if (!queryParams) {
    console.error("Can not fetch stat boxes, query params are not set.");
    return;
  }

  try {
    const r = await apolloClient.query<
      FetchStatBoxesQuery,
      FetchStatBoxesQueryVariables
    >({
      query,
      variables: queryParams,
      fetchPolicy: "network-only",
    });

    const items =
      r.data.productPrice.customers.byId?.statisticsByFilter.items ?? [];
    const expectedStatGroupItems =
      r.data.productPrice.customers.byId?.expectedStatGroupVolume.volumes ?? [];
    dispatch(
      statBoxesRedux.actions.setQueryData(mergeStatBoxQueryData({}, items))
    );
    const itemsWithExpectedStatGroupVolume = itemWithExpectedStatGroupVolume(
      items,
      expectedStatGroupItems
    );

    const transformedData = calculateCustomerRouteStatisticsTable(
      itemsWithExpectedStatGroupVolume,
      queryParams.customerId
    );

    dispatch(statBoxesRedux.actions.setStatBoxesData(transformedData));
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(statBoxesRedux.actions.setNotLoading());
  }
};

const itemWithExpectedStatGroupVolume = (
  items: CustomerStatisticsByFilterItemsFragment[],
  expectedStatGroupVolume: ExpectedStatGroupVolumeItemsFragment[]
) => {
  return items.map((item) => {
    const match = expectedStatGroupVolume.find(
      (esgv) =>
        esgv.sailingCategoryCode === item.sailingCategoryCode &&
        esgv.unitCategoryCode === item.unitCategoryCode
    );
    return {
      ...item,
      expectedStatVolume: match?.expectedStatGroupVolume ?? 0,
    };
  });
};
